/**
 * @fileOverview Batch loader utility for efficient pagination of large datasets
 * @description Provides a generic batch loading mechanism for incrementally loading and displaying
 * large lists of items in chunks. Implements lazy loading pattern with configurable batch sizes,
 * loading indicators, and destruction handling.
 * 
 * @module batch-loader.util
 */

/**
 * @class BatchLoader
 * @template T Generic type parameter representing the data type of items being loaded
 * @description Utility class for managing incremental batch loading of data. Efficiently loads
 * large datasets in configurable chunks, updates a target list, and provides loading state management.
 * Useful for optimizing performance when rendering large lists (e.g., infinite scroll, pagination).
 * 
 * Features:
 * - Generic type support for any data type
 * - Configurable batch size with sensible defaults
 * - Loading state tracking and callbacks
 * - Destruction lifecycle management
 * - Automatic delay between batches for smooth UX
 * - Prevents memory leaks through proper cleanup
 * 
 * @example
 * // Initialize batch loader for a list of products
 * const batchLoader = new BatchLoader<Product>(
 *   () => this.allProducts,                    // source function
 *   (list) => this.displayedProducts = list,   // update callback
 *   20,                                        // batch size
 *   (isLoading) => this.showSpinner = isLoading // loader callback
 * );
 * 
 * // Load next batch when user scrolls to bottom
 * await batchLoader.loadNextBatch();
 */
export class BatchLoader<T> {
  /**
   * @property {boolean} destroyed
   * @description Flag indicating whether the batch loader has been destroyed.
   * When true, prevents further batch loading to avoid memory leaks and unnecessary processing.
   */
  private destroyed = false;

  /**
   * @property {boolean} isLoadingFlag
   * @description Flag tracking the current loading state of batch operations.
   * True while a batch is being loaded, false when idle.
   */
  private isLoadingFlag = false;

  /**
   * @property {number} startIndex
   * @description Current starting index for the next batch slice operation.
   * Incremented after each batch load to track position in source data.
   */
  private startIndex = 0;

  /**
   * @property {T[]} targetList
   * @description Accumulator array containing all loaded items so far.
   * Grows incrementally as each batch is loaded and combined.
   */
  private targetList: T[] = [];

  /**
   * @property {number} defaultBatchSize
   * @description Default number of items to load in each batch (12 items).
   * Used when no custom batch size is provided to the constructor.
   */
  private readonly defaultBatchSize = 12;

  /**
   * @method constructor
   * @description Initializes the batch loader with required and optional configuration parameters.
   * Sets up the data source, update callback, and optional batch size and loading indicators.
   * 
   * @param {Function} source - Callback function that returns the complete source data array to be loaded in batches
   * @param {Function} updateList - Callback function invoked with the accumulated list whenever a batch is loaded
   * @param {number} [batchSize] - Optional custom batch size. Defaults to 12 if not provided
   * @param {Function} [setLoader] - Optional callback to set loading state (true/false) for UI updates
   */
  constructor(
    private source: () => T[],
    private updateList: (list: T[]) => void,
    private batchSize?: number,
    private setLoader?: (isLoading: boolean) => void
  ) {}

  /**
   * @method recordsSize
   * @description Getter that returns the effective batch size for loading operations.
   * Returns the custom batch size if provided during initialization, otherwise returns the default.
   * 
   * @returns {number} The batch size to use for loading (either custom or default value of 12)
   */
  public get recordsSize(): number {
    return this.batchSize ?? this.defaultBatchSize;
  }

  /**
   * @method markAsDestroyed
   * @description Marks the batch loader as destroyed to prevent further loading operations.
   * Should be called in component cleanup (ngOnDestroy) to prevent memory leaks and
   * background processing after component is removed from the DOM.
   * 
   * @returns {void}
   */
  public markAsDestroyed(): void {
    this.destroyed = true;
  }

  /**
   * @method isLoading
   * @description Getter that returns the current loading state of the batch loader.
   * Useful for UI components to show/hide loading indicators or spinners.
   * 
   * @returns {boolean} True if a batch is currently loading, false otherwise
   */
  public isLoading(): boolean {
    return this.isLoadingFlag;
  }

  /**
   * @method reset
   * @description Resets the batch loader to its initial state, clearing all loaded data and counters.
   * Useful when filtering, sorting, or refreshing the source data. Optionally suppresses
   * the update callback to avoid unnecessary UI updates during batch loading setup.
   * 
   * @param {boolean} [suppressUpdate=false] - If true, skips calling the updateList callback.
   * Set to true when resetting during batch operations to avoid intermediate updates.
   * @returns {void}
   */
  public reset(suppressUpdate: boolean = false): void {
    this.startIndex = 0;
    this.targetList = [];
    this.isLoadingFlag = false;
    this.destroyed = false;
    if (!suppressUpdate) {
      this.updateList([]);
    }
  }

  /**
   * @method loadNextBatch
   * @description Loads the next batch of items from the source data and appends them to the target list.
   * Uses progressive delays for better UX: no delay for first batch, 1 second for subsequent batches.
   * Automatically calls the updateList and setLoader callbacks with results.
   * 
   * Checks destroyed flag and source boundaries before loading to prevent errors.
   * Uses structuredClone to ensure proper data independence between updates.
   * 
   * @async
   * @param {boolean} [skipLoader=false] - If true, skips updating the loading state indicator.
   * Useful when loading multiple batches in quick succession without visual feedback for each.
   * @returns {Promise<void>} Resolves when the batch has been loaded and callbacks have been invoked
   * 
   * @example
   * // Load next batch and show loading indicator
   * await batchLoader.loadNextBatch();
   * 
   * // Load multiple batches without per-batch loading indicator
   * await batchLoader.loadNextBatch(true);
   * await batchLoader.loadNextBatch(true);
   */
  public async loadNextBatch(skipLoader: boolean = false): Promise<void> {
    if (this.destroyed) return;
    const sourceList = this.source();
    if (this.startIndex >= sourceList.length) return;
    if (!skipLoader) {
      this.isLoadingFlag = true;
      this.setLoader?.(true);
    }
    // Use 0ms delay for the very first batch, then 1000ms for others
    const delay = this.startIndex === 0 ? 0 : 1000;
    await new Promise<void>((resolve) =>
      setTimeout(() => {
        if (this.destroyed) {
          resolve();
          return;
        }
        const nextBatch = sourceList.slice(this.startIndex, this.startIndex + this.recordsSize);
        this.targetList = [...this.targetList, ...nextBatch];
        this.updateList(structuredClone(this.targetList));
        this.startIndex += this.recordsSize;
        this.isLoadingFlag = false;
        this.setLoader?.(false);
        resolve();
      }, delay)
    );
  }
}
